/**
 * Sport presentation catalogs — UI copy / format options only.
 * Prefer sport module createMatchConfiguration(); this map is for tests / legacy lookups.
 *
 * Slicer lists are ascending by value; `defaultSetsToWin` / `defaultPointsToWin` are
 * the sport’s standard start values (not necessarily the first list entry).
 */

import { DEFAULT_SPORT, SPORT_IDS, normalizeSportId } from "./sportTypes";
import { PICKLEBALL_MATCH_FORMAT } from "../../sports/pickleball/config/matchFormat";
import { TENNIS_MATCH_FORMAT } from "../../sports/tennis/config/matchFormat";
import { TABLE_TENNIS_MATCH_FORMAT } from "../../sports/table-tennis/config/matchFormat";

/**
 * @typedef {{ id: string, title: string, sub?: string, matchType: 'singles' | 'doubles', playersPerSide: number }} MatchTypeOption
 * @typedef {{ setsToWin: number, title: string }} SetsOption
 * @typedef {{ mode: number | 'custom', title: string }} PointsOption
 *
 * @typedef {{
 *   sportId: string,
 *   displayName: string,
 *   matchTypes: MatchTypeOption[],
 *   setsOptions: SetsOption[],
 *   pointsPresets: PointsOption[],
 *   customPoints: { min: number, max: number, label: string, placeholder: string },
 *   customSets: { min: number, max: number, label: string },
 *   defaultSetsToWin?: number,
 *   defaultPointsToWin?: number,
 *   courtPresentationId: string,
 *   intervalPresentationId: string,
 * }} SportMatchFormatCatalog
 */

/** @type {SportMatchFormatCatalog} */
export const BADMINTON_MATCH_FORMAT = Object.freeze({
  sportId: SPORT_IDS.BADMINTON,
  displayName: "Badminton",
  matchTypes: [
    { id: "singles", title: "Singles", sub: "1v1", matchType: "singles", playersPerSide: 1 },
    { id: "doubles", title: "Doubles", sub: "2v2", matchType: "doubles", playersPerSide: 2 },
  ],
  setsOptions: [
    { setsToWin: 1, title: "1 Set" },
    { setsToWin: 2, title: "Best of 3" },
    { setsToWin: 3, title: "Best of 5" },
  ],
  pointsPresets: [
    { mode: 15, title: "First to 15" },
    { mode: 21, title: "First to 21" },
    { mode: "custom", title: "Custom" },
  ],
  /** BWF casual default — Best of 3 to 21 (not first slicer entry). */
  defaultSetsToWin: 2,
  defaultPointsToWin: 21,
  customPoints: {
    min: 1,
    max: 50,
    label: "Custom points",
    placeholder: "1–50",
  },
  customSets: {
    min: 1,
    max: 11,
    label: "Custom best of",
    oddOnly: true,
  },
  courtPresentationId: "badminton.court.v1",
  intervalPresentationId: "badminton.bwf_interval.v1",
});

export { PICKLEBALL_MATCH_FORMAT, TENNIS_MATCH_FORMAT, TABLE_TENNIS_MATCH_FORMAT };

/** @type {Record<string, SportMatchFormatCatalog>} */
const CATALOGS = Object.freeze({
  [SPORT_IDS.BADMINTON]: BADMINTON_MATCH_FORMAT,
  [SPORT_IDS.PICKLEBALL]: PICKLEBALL_MATCH_FORMAT,
  [SPORT_IDS.TENNIS]: TENNIS_MATCH_FORMAT,
  [SPORT_IDS.TABLE_TENNIS]: TABLE_TENNIS_MATCH_FORMAT,
});

/**
 * Ascending slicer order: numeric presets first, Custom last.
 * @param {PointsOption[]} presets
 */
export function sortPointsPresetsAscending(presets) {
  return [...(presets || [])].sort((a, b) => {
    if (a.mode === "custom") return 1;
    if (b.mode === "custom") return -1;
    return Number(a.mode) - Number(b.mode);
  });
}

/**
 * @param {SetsOption[]} options
 */
export function sortSetsOptionsAscending(options) {
  return [...(options || [])].sort((a, b) => a.setsToWin - b.setsToWin);
}

/**
 * @param {string | null | undefined} sportId
 * @returns {SportMatchFormatCatalog}
 */
export function getSportMatchFormatCatalog(sportId) {
  const id = normalizeSportId(sportId);
  const raw = CATALOGS[id] || CATALOGS[DEFAULT_SPORT];
  return {
    ...raw,
    setsOptions: sortSetsOptionsAscending(raw.setsOptions),
    pointsPresets: sortPointsPresetsAscending(raw.pointsPresets),
  };
}

/**
 * @param {string | null | undefined} sportId
 * @returns {{ setsToWin: number, pointsToWin: number }}
 */
export function getDefaultMatchFormatForSport(sportId) {
  const cat = getSportMatchFormatCatalog(sportId);
  const setsToWin =
    typeof cat.defaultSetsToWin === "number"
      ? cat.defaultSetsToWin
      : cat.setsOptions?.[0]?.setsToWin ?? 2;
  const pointsToWin =
    typeof cat.defaultPointsToWin === "number"
      ? cat.defaultPointsToWin
      : cat.pointsPresets?.find((p) => typeof p.mode === "number")?.mode ?? 21;
  return { setsToWin, pointsToWin };
}

/**
 * Coerce setup / live points to a positive integer (keeps custom values like 20 in sync).
 * @param {unknown} value
 * @param {number} [fallback]
 */
export function normalizePointsToWin(value, fallback = 21) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.min(100, Math.trunc(n));
}

/**
 * Room session default sport until Room carries sportId.
 * @returns {typeof SPORT_IDS.BADMINTON}
 */
export function getSessionSportId() {
  return DEFAULT_SPORT;
}

/**
 * Players per side for a tournament Event format — from Sport match-type catalog only.
 * @param {'singles'|'doubles'|string} formatId Event `competition.format` (matches catalog id / matchType)
 * @param {string | null | undefined} sportId
 * @returns {number}
 */
export function resolvePlayersPerSideFromMatchFormat(formatId, sportId) {
  const format = String(formatId || "").trim();
  if (!format) {
    throw new Error("Event competition.format is required to resolve players per side.");
  }
  const catalog = getSportMatchFormatCatalog(sportId);
  const opt = catalog.matchTypes.find(
    (m) => m.id === format || m.matchType === format
  );
  if (!opt || typeof opt.playersPerSide !== "number") {
    throw new Error(
      `Sport "${catalog.sportId}" has no match type "${format}" with playersPerSide.`
    );
  }
  return opt.playersPerSide;
}
